import {useAtom} from 'jotai';
import {useEffect, useState} from 'react';
import {AddressItemType} from '@/map/@types';
import {useCoordToAddress} from '@/map/api';
import {MAP_QUERY_DEBOUNCE_SHORT_MS} from '@/map/constants';
import {mapMobileCenterAtom, mobileAnalysisPointAtom} from '@/map/atom';
import {MobileMapAgent} from '@/map/enums';
import {convertToCoordFromPointCoords} from '@/map/utils';
import {AddressSearch} from './AddressSearch';
import {AnalysisDrawer} from './AnalysisDrawer';
import {useAndroidBackHandler} from './AndroidBackHandler/useAndroidBackHandler';
import {SearchCard} from './Card';
import {LocationMarker} from './Marker';

interface IProps {
  map?: naver.maps.Map;
  agent: MobileMapAgent;
}

export const BottomSection = ({map, agent}: IProps) => {
  const [mapCenter] = useAtom(mapMobileCenterAtom(agent));
  const [openSearchPanel, setOpenSearchPanel] = useState<boolean>(false);
  const [searchSelected, setSearchSelected] = useState<{
    keyword: string;
    coords: naver.maps.Coord;
  }>();

  const [analysisPoint, setAnalysisPoint] = useAtom(mobileAnalysisPointAtom);
  const center = mapCenter as naver.maps.LatLng;

  const isAnalysisScreen = !!analysisPoint?.center;

  const onSearchSelected = (selected: {keyword: string; item: AddressItemType}) => {
    if (!selected || !map || !selected.item?.location?.coordinates) {
      return;
    }

    const coords = convertToCoordFromPointCoords(selected.item.location.coordinates);

    if (!coords) {
      return;
    }

    map.setCenter(coords);

    setAnalysisPoint(null);
    setSearchSelected({coords, keyword: selected.keyword});
    setOpenSearchPanel(false);
  };

  const {data, isError} = useCoordToAddress(
    center && !analysisPoint?.center
      ? {
          lat: center.lat(),
          lng: center.lng(),
        }
      : undefined,
    MAP_QUERY_DEBOUNCE_SHORT_MS
  );

  const onAnalysisButtonClick = () => {
    if (!center) {
      return;
    }

    if (data || (isError && searchSelected?.coords)) {
      const keyword = searchSelected?.keyword || data?.address;

      if (keyword) {
        setAnalysisPoint({
          center,
          keyword,
          radius: 500,
        });
      }
    }
  };

  const onOpenSearchPanel = () => setOpenSearchPanel(true);

  const clearAnalysisPoint = () => {
    if (analysisPoint?.center && map) {
      // 이전 주소/키워드로 다시 세팅
      const coords = analysisPoint.center;

      if (map.getBounds().hasPoint(coords)) {
        map.setCenter(coords);
        setSearchSelected({
          coords,
          keyword: analysisPoint.keyword,
        });
      }
    }
    setAnalysisPoint(null);
  };

  useEffect(() => {
    if (searchSelected && !center.equals(searchSelected.coords)) {
      setSearchSelected(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center]);

  useAndroidBackHandler(() => {
    if (isAnalysisScreen) {
      clearAnalysisPoint();
      return true;
    }

    return false;
  }, isAnalysisScreen); // 분석상태일때만 핸들러 등록

  if (openSearchPanel) {
    return (
      <AddressSearch
        initialValue={analysisPoint?.keyword}
        onSelected={onSearchSelected}
        onPrev={() => {
          setOpenSearchPanel(false);
          clearAnalysisPoint();
        }}
      />
    );
  }
  if (analysisPoint?.center) {
    return (
      <AnalysisDrawer map={map} item={analysisPoint} onClose={clearAnalysisPoint} onSearchFocus={onOpenSearchPanel} />
    );
  }

  return (
    <>
      <LocationMarker hasTooltip={!!searchSelected?.keyword} />
      <SearchCard
        text={searchSelected?.keyword || data?.address}
        isError={isError}
        onFocus={onOpenSearchPanel}
        onClick={onAnalysisButtonClick}
      />
    </>
  );
};
